import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { SERVICES } from "@/data/services";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import { trackPhoneClick } from "@/lib/track";

const Services = () => {
  return (
    <div data-testid="services-page">
      <Seo
        title="Engineering Services"
        description="Full-service industrial and commercial engineering: PPMs, EICRs, electrical installation, hydraulics, pneumatics, water systems, automation, welding, fabrication, fire alarms, emergency lighting and bespoke machinery."
        path="/services"
      />
      <section className="relative overflow-hidden">
        <PhotoBackdrop src="/gallery/spindle-assembly.jpg" position="center 45%" />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 relative">
          <p className="eyebrow">Engineering Services</p>
          <h1
            className="font-display mt-6 text-white"
            style={{ fontSize: "clamp(44px, 6vw, 88px)", lineHeight: 0.95 }}
            data-testid="services-heading"
          >
            EVERYTHING YOU NEED,
            <br />
            <span className="gradient-blue-text">UNDER ONE ROOF.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-[color:var(--lt-text-dim)]">
            A comprehensive suite of mechanical and electrical disciplines —
            including hydraulics, pneumatics, water systems, automation, welding,
            fabrication, fire alarms, emergency lighting and bespoke machinery —
            delivered by a small team of highly experienced engineers who care
            about your assets as much as you do.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl">
            <div className="glass-card rounded-xl p-5" data-testid="services-intro-1">
              <p className="eyebrow">Single supplier</p>
              <p className="mt-3 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
                One accountable team across mechanical, electrical, controls,
                fabrication and fire safety — so you stop coordinating three
                sub-contractors and start getting things fixed. Fewer handoffs
                mean quicker resolution and one clean paper trail per asset.
              </p>
            </div>
            <div className="glass-card rounded-xl p-5" data-testid="services-intro-2">
              <p className="eyebrow">Reactive · Planned · Bespoke</p>
              <p className="mt-3 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
                From a broken conveyor at 03:00 to a rolling 12-month PPM
                across a multi-site portfolio, to a fully engineered bespoke
                production machine — we're set up to handle it. Every job is
                engineer-led from survey to sign-off.
              </p>
            </div>
            <div className="glass-card rounded-xl p-5" data-testid="services-intro-3">
              <p className="eyebrow">Independently certified</p>
              <p className="mt-3 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
                Externally audited to ISO 9001, ISO 14001 and ISO 45001, plus
                SafeContractor, IPAF and City & Guilds accreditations. Your
                procurement and H&amp;S teams get the paperwork they need
                without chasing us for it.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-8 space-y-6">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          const reverse = i % 2 === 1;
          return (
            <article
              id={s.slug}
              key={s.slug}
              className="service-card rounded-2xl p-7 lg:p-10 scroll-mt-28"
              data-testid={`service-detail-${s.slug}`}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-start ${reverse ? "lg:[direction:rtl]" : ""}`}>
                <div className="lg:col-span-7 [direction:ltr]">
                  <div className="flex items-center gap-3">
                    <span className="icon-tile" style={{ width: 44, height: 44 }}>
                      <Icon size={20} />
                    </span>
                    <p className="eyebrow" style={{ margin: 0 }}>
                      {String(i + 1).padStart(2, "0")} / {SERVICES.length.toString().padStart(2, "0")}
                    </p>
                  </div>
                  <h2
                    className="font-display mt-5 text-white"
                    style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 0.98 }}
                  >
                    {s.title.toUpperCase()}
                  </h2>
                  <p className="mt-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)] max-w-2xl">
                    {s.blurb}
                  </p>
                  <Link
                    to="/contact"
                    className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white border-b border-[color:var(--lt-border-strong)] pb-0.5 hover:text-[color:var(--lt-blue-soft)] transition-colors"
                    data-testid={`service-cta-${s.slug}`}
                  >
                    Enquire about {s.title} <ArrowRight size={14} />
                  </Link>
                </div>
                <div className="lg:col-span-5 [direction:ltr]">
                  <div className="grid grid-cols-1 gap-3">
                    {s.bullets.map((b) => (
                      <div
                        key={b}
                        className="flex items-start gap-3 rounded-lg p-4"
                        style={{
                          background: "rgba(6,11,24,0.5)",
                          border: "1px solid var(--lt-border)",
                        }}
                      >
                        <CheckCircle2
                          size={18}
                          className="mt-0.5 flex-shrink-0"
                          color="var(--lt-blue-bright)"
                        />
                        <span className="text-sm text-white">{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-20">
        <div className="glow-card rounded-2xl p-10 lg:p-14 text-center relative overflow-hidden">
          <PhotoBackdrop src="/gallery/db-panel-2.jpg" position="center 50%" dim={1.05} />
          <div className="relative">
          <p className="eyebrow justify-center" style={{ display: "inline-flex" }}>Speak to an engineer</p>
          <h3
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(28px, 3.8vw, 46px)", lineHeight: 1 }}
          >
            NOT SURE WHICH SERVICE YOU NEED?
          </h3>
          <p className="mt-4 text-[color:rgba(230,236,255,0.78)] max-w-xl mx-auto">
            Tell us about your site and we&apos;ll recommend the right scope — no obligation.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a
              href="tel:+447599474730"
              onClick={() => trackPhoneClick("services_page_bottom")}
              className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="services-page-call"
            >
              <Phone size={15} /> +44 7599 474730
            </a>
            <Link
              to="/contact"
              className="btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="services-page-contact"
            >
              Request a Quote <ArrowRight size={15} />
            </Link>
          </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
